import { Fragment } from 'react'
import { Trash2 } from 'lucide-react'

import type { PackagingDraftItem } from '@/lib/packaging-draft'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface OrderMaterialSubRowsProps {
    /** Vật tư gắn dòng SP (đã lọc theo line_key ở form cha). */
    materials: PackagingDraftItem[]
    /** Giá vật tư hiệu lực theo trạng thái tách/gộp của dòng SP. */
    effectivePriceOf: (p: PackagingDraftItem) => number
    onQtyChange: (materialId: string, lineKey: string, value: string) => void
    onPriceChange: (materialId: string, lineKey: string, value: string) => void
    onDiscountChange: (
        materialId: string,
        lineKey: string,
        value: string,
    ) => void
    onRemove: (materialId: string, lineKey: string) => void
}

/**
 * Dòng con "vật tư" render inline dưới 1 dòng sản phẩm (create + edit dùng
 * chung để đảm bảo hiển thị đồng nhất). Mọi dòng đều sửa được SL/giá/CK, hiện
 * tồn + cảnh báo thiếu, và xoá được.
 *
 * Trả về danh sách <tr> nên PHẢI đặt trong <tbody> của form cha.
 */
export function OrderMaterialSubRows({
    materials,
    effectivePriceOf,
    onQtyChange,
    onPriceChange,
    onDiscountChange,
    onRemove,
}: OrderMaterialSubRowsProps) {
    return (
        <Fragment>
            {materials.map((p) => {
                const effPrice = effectivePriceOf(p)
                const lineAmount =
                    effPrice *
                    p.quantity *
                    (1 - (p.discount_percent ?? 0) / 100)
                const shortage = p.quantity > p.available
                return (
                    <tr
                        key={`mat-${p.line_key}-${p.packaging_material_id}`}
                        className={`border-b align-middle ${
                            shortage ? 'bg-amber-50' : 'bg-muted/20'
                        }`}
                    >
                        <td className="p-2 pl-6 text-sm">
                            <span className="inline-flex items-center gap-1">
                                <span className="text-muted-foreground">↳</span>
                                {p.material_name}
                            </span>
                            <div className="text-xs text-muted-foreground">
                                {m.packaging_editor_col_available()}:{' '}
                                {p.available.toLocaleString('vi-VN', {
                                    maximumFractionDigits: 3,
                                })}
                                {shortage ? (
                                    <span className="ml-1 text-amber-700">
                                        ({m.packaging_editor_badge_shortage()})
                                    </span>
                                ) : null}
                            </div>
                        </td>
                        <td className="p-2 text-sm text-muted-foreground whitespace-nowrap">
                            {p.material_unit || '—'}
                        </td>
                        <td className="p-2 text-sm">
                            <Input
                                type="number"
                                min={0}
                                step="0.001"
                                inputMode="decimal"
                                value={p.quantity}
                                onChange={(e) =>
                                    onQtyChange(
                                        p.packaging_material_id,
                                        p.line_key,
                                        e.target.value,
                                    )
                                }
                                className="w-full"
                                aria-label={`${p.material_name} SL`}
                            />
                        </td>
                        <td className="p-2 text-sm">
                            <Input
                                type="number"
                                min={0}
                                step="1000"
                                inputMode="numeric"
                                value={effPrice}
                                onChange={(e) =>
                                    onPriceChange(
                                        p.packaging_material_id,
                                        p.line_key,
                                        e.target.value,
                                    )
                                }
                                className="w-full"
                                aria-label={`${p.material_name} ${m.order_unit_price()}`}
                            />
                        </td>
                        <td className="p-2">
                            <Input
                                type="number"
                                min={0}
                                max={100}
                                step="1"
                                inputMode="numeric"
                                value={p.discount_percent}
                                onChange={(e) =>
                                    onDiscountChange(
                                        p.packaging_material_id,
                                        p.line_key,
                                        e.target.value,
                                    )
                                }
                                className="w-full"
                                aria-label={`${p.material_name} CK %`}
                            />
                        </td>
                        <td className="p-2 text-right text-sm font-medium whitespace-nowrap">
                            {formatVND(lineAmount)}
                        </td>
                        <td className="p-2">
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={() =>
                                    onRemove(
                                        p.packaging_material_id,
                                        p.line_key,
                                    )
                                }
                                className="text-destructive"
                                aria-label={`${m.common_aria_delete()} ${p.material_name}`}
                            >
                                <Trash2 className="size-4" />
                            </Button>
                        </td>
                    </tr>
                )
            })}
        </Fragment>
    )
}
